import { CreateThingCard } from "@/components/create-thing-card";
import { EmptyState } from "@/components/page-layout";
import { RequireScope } from "@/components/require-scope";
import { Cards } from "@/components/ui/Card";
import { Text } from "@/components/ui/Text";
import { Toolset } from "@/lib/toolTypes";
import { useRoutes } from "@/routes";
import { PromptTemplate } from "@gram/client/models/components/prompttemplate.js";
import { useUpdateToolsetMutation } from "@gram/client/react-query/updateToolset.js";
import { Button } from "@/components/ui/Button";
import { Stack } from "@/components/ui/Stack";
import { useState } from "react";
import { PromptTemplateCard } from "../prompts/Prompts";
import { ToolsetsGraphic } from "./ToolsetsEmptyState";
import { usePrompts } from "../prompts/usePrompts";
import { PromptSelectPopover } from "../prompts/PromptSelectPopover";

export function PromptsTabContent({
  toolset,
  updateToolsetMutation,
}: {
  toolset: Toolset;
  updateToolsetMutation: ReturnType<typeof useUpdateToolsetMutation>;
}): JSX.Element {
  const { prompts: allPrompts } = usePrompts();
  const toolsetPrompts = toolset.promptTemplates;
  const [promptSelectPopoverOpen, setPromptSelectPopoverOpen] = useState(false);
  const routes = useRoutes();

  const currentPromptNames = toolsetPrompts?.map((t) => t.name) ?? [];

  const addPromptToToolset = (prompt: PromptTemplate) => {
    if (currentPromptNames.includes(prompt.name)) {
      return;
    }

    updateToolsetMutation.mutate({
      request: {
        slug: toolset.slug,
        updateToolsetRequestBody: {
          promptTemplateNames: [...currentPromptNames, prompt.name],
        },
      },
    });
  };

  const removePromptFromToolset = (promptName: string) => {
    updateToolsetMutation.mutate({
      request: {
        slug: toolset.slug,
        updateToolsetRequestBody: {
          promptTemplateNames: currentPromptNames.filter(
            (name) => name !== promptName,
          ),
        },
      },
    });
  };

  const hasPrompts = toolsetPrompts && toolsetPrompts.length > 0;

  if (!hasPrompts) {
    return (
      <EmptyState
        heading="No prompts yet"
        description="Prompt templates are ready-made prompts your MCP server offers to connected clients like Claude Desktop or Cursor, so users can start a common task without typing it out themselves."
        nonEmptyProjectCTA={
          <RequireScope scope="mcp:write" level="component">
            <Stack gap={2} direction="horizontal">
              <routes.prompts.newPrompt.Link>
                <Button size="sm">Create Prompt</Button>
              </routes.prompts.newPrompt.Link>
            </Stack>
          </RequireScope>
        }
        graphic={<ToolsetsGraphic />}
        graphicClassName="scale-90"
      />
    );
  }

  return (
    <>
      <Cards isLoading={!toolset}>
        {toolsetPrompts?.map((prompt) => (
          <PromptTemplateCard
            key={prompt.name}
            template={prompt}
            onDelete={() => removePromptFromToolset(prompt.name)}
            deleteLabel="Remove from MCP server"
          />
        ))}
      </Cards>
      <RequireScope scope="mcp:write" level="section">
        <Stack
          gap={3}
          direction={"horizontal"}
          align={"center"}
          className="w-full max-w-4xl"
        >
          {allPrompts && allPrompts?.length > 0 && (
            <>
              <PromptSelectPopover
                open={promptSelectPopoverOpen}
                setOpen={setPromptSelectPopoverOpen}
                onSelect={(prompt) => addPromptToToolset(prompt)}
              >
                <div className="w-full">
                  <CreateThingCard className="mb-0!">
                    + Add Prompt
                  </CreateThingCard>
                </div>
              </PromptSelectPopover>
              <Text muted>or</Text>
            </>
          )}
          <div className="w-full">
            <routes.prompts.newPrompt.Link>
              <CreateThingCard className="mb-0!">
                + Create Prompt
              </CreateThingCard>
            </routes.prompts.newPrompt.Link>
          </div>
        </Stack>
      </RequireScope>
    </>
  );
}
